import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  AT_LEAST_ONE_LOWER_CASE,
  AT_LEAST_ONE_NUMBER,
  AT_LEAST_ONE_SPECIAL_CHARACTER,
  AT_LEAST_ONE_UPPER_CASE,
  AT_LEAST_TEN_CHARACTERS,
} from 'src/components/Forms';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ResendInvitePasswordValidation } from './resend-invite-password-validation';
import {
  PasswordsValidation,
  ResendInviteWithPasswordFormFields,
  ResendInviteWithPasswordFormModel,
} from './types';

export const ResentInviteWithPasswordModal = () => {
  const theme = useTheme();
  const { closeModal } = useModalMethods(Modals.ResentInviteWithPassword);
  const { isOpened, modalProps } = useModalState(Modals.ResentInviteWithPassword);

  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () => setShowConfirmPassword((show) => !show);

  const [formValid, setFormValid] = useState<boolean>(false);
  const [passwordsValidationResults, setPasswordsValidationResults] =
    useState<PasswordsValidation | null>(null);

  const formMethods = useForm<ResendInviteWithPasswordFormModel>({
    mode: 'all',
    values: {
      [ResendInviteWithPasswordFormFields.Password]: '',
      [ResendInviteWithPasswordFormFields.PasswordConfirm]: '',
    },
  });

  const { formState, reset, control, handleSubmit } = formMethods;

  useEffect(() => {
    const { unsubscribe } = formMethods.watch((value) => {
      const password = value[ResendInviteWithPasswordFormFields.Password];
      const confirmPassword = value[ResendInviteWithPasswordFormFields.PasswordConfirm];

      if (!password && !confirmPassword) {
        setPasswordsValidationResults(null);
        setFormValid(false);
        return;
      }
      const passwordValidationResults = validatePasswordField(password!);
      const confirmPasswordValidationResults = validatePasswordField(confirmPassword!);

      const passwordMatch = password === confirmPassword;

      setPasswordsValidationResults({
        ...passwordValidationResults,
        passwordsEqual: passwordMatch,
      });

      const isPasswordValid = passwordFieldIsValid(passwordValidationResults);
      const isConfirmPasswordValid = passwordFieldIsValid(confirmPasswordValidationResults);

      const isValid = isPasswordValid && isConfirmPasswordValid && passwordMatch;
      setFormValid(isValid);
    });

    return () => unsubscribe();
  }, [formMethods, setFormValid, setPasswordsValidationResults]);

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(
    (formValues: ResendInviteWithPasswordFormModel) => {
      modalProps?.onSubmit(
        modalProps.userId,
        formValues[ResendInviteWithPasswordFormFields.Password],
      );
      closeModalAndResetForm();
    },
    [modalProps, closeModalAndResetForm],
  );

  const onClose = useCallback(() => {
    closeModalAndResetForm();
  }, [closeModalAndResetForm]);

  return (
    <Dialog open={isOpened} onClose={onClose} fullWidth>
      <DialogTitle>
        <Typography>Create a temporary password and send it to the user.</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack flexDirection="column">
          <FormProvider {...formMethods}>
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '50vh',
                justifyContent: 'space-between',
              }}
              onSubmit={handleSubmit(onFormSubmit)}
            >
              <Stack>
                <FormControl
                  sx={{
                    minWidth: '100%',
                    margin: '0.5rem 0',
                  }}
                  variant="outlined"
                >
                  <Controller
                    control={control}
                    name={ResendInviteWithPasswordFormFields.Password}
                    render={({ field }) => (
                      <>
                        <InputLabel htmlFor="password">Password</InputLabel>
                        <OutlinedInput
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          error={
                            !!formState.errors[ResendInviteWithPasswordFormFields.Password]
                          }
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showPassword ? 'hide the password' : 'display the password'
                                }
                                onClick={handleClickShowPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Password"
                          {...field}
                        />
                      </>
                    )}
                  />
                </FormControl>
                <FormControl
                  sx={{
                    minWidth: '100%',
                    margin: '0.5rem 0',
                  }}
                  variant="outlined"
                >
                  <Controller
                    control={control}
                    name={ResendInviteWithPasswordFormFields.PasswordConfirm}
                    render={({ field }) => (
                      <>
                        <InputLabel htmlFor="confirm-password">Confirm</InputLabel>
                        <OutlinedInput
                          id="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          endAdornment={
                            <InputAdornment position="end">
                              <IconButton
                                aria-label={
                                  showConfirmPassword
                                    ? 'hide the password'
                                    : 'display the password'
                                }
                                onClick={handleClickShowConfirmPassword}
                                edge="end"
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }
                          label="Password"
                          {...field}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Stack>
              <ResendInvitePasswordValidation validationResults={passwordsValidationResults} />
              <Button
                type="submit"
                disabled={!formValid}
                sx={{ alignSelf: 'flex-end', marginTop: theme.spacing(2) }}
              >
                Submit
              </Button>
            </form>
          </FormProvider>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

function validatePasswordField(password: string): PasswordsValidation {
  return {
    atLeastTenCharacters: AT_LEAST_TEN_CHARACTERS.test(password),
    atLeastOneNumber: AT_LEAST_ONE_NUMBER.test(password),
    atLeastOneLowerCaseLetter: AT_LEAST_ONE_LOWER_CASE.test(password),
    atLeastOneUpperCaseLetter: AT_LEAST_ONE_UPPER_CASE.test(password),
    atLeastOneSpecialCharacter: AT_LEAST_ONE_SPECIAL_CHARACTER.test(password),
  };
}

function passwordFieldIsValid(data: PasswordsValidation): boolean {
  return Object.keys(data).every((key) => data[key as keyof PasswordsValidation]);
}
